import React, { useState, useEffect } from "react";
import { supabase } from "../supabaseClient";
import { useAuth } from "../hooks/useAuth";
import "../DashboardStyles.css";

// ── Colors (match CSS vars) ───────────────────────────────────────────────────
const C = {
  navy:   "#122326",
  slate:  "#213A3E",
  terra:  "#A84F30",
  cream:  "#F0EFEB",
  warm:   "#F7F6F3",
  sand:   "#E2E0DB",
  white:  "#FFFFFF",
  g1:     "#2563EB",  // Goal 1 — blue
  g2:     "#059669",  // Goal 2 — green
  g3:     "#7C3AED",  // Goal 3 — purple
  g4:     "#D97706",  // Goal 4 — amber
};

// ── KPI data ──────────────────────────────────────────────────────────────────
// The 31 performance measures in Chapter 9 of the approved 2026–2031 CEDS.
// IDs are the Supabase `ceds_kpi_values.kpi_id` keys: IDs from the draft plan were
// kept where the measure is unchanged so saved values carry over; new or redefined
// measures got new IDs. `partOf` marks an extra row that reports alongside another
// measure (it does not count toward the 31).
const GOALS = [
  {
    id: "g1", num: "Goal 1", color: C.g1,
    title: "Strengthen Foundations",
    sub: "Housing & Childcare",
    objectives: [
      {
        id: "obj1_1", label: "Obj 1.1 — Housing (target: 25 workforce units/yr via PADD programs, beginning 2027)",
        kpis: [
          { id: "1_1_w", label: "Workforce housing units completed through PADD-administered or PADD-assisted programs (report share outside Scotts Bluff Co.)", unit: "units/yr", defaultTarget: "25" },
          { id: "1_1_p", label: "Residential units authorized by building permit, 11-county total (Census BPS)", unit: "units/yr" },
          { id: "1_1_b", label: "Housing-related grant dollars awarded to Panhandle applicants", unit: "$" },
          { id: "1_1_c", label: "Municipalities with a housing study or plan adopted in the prior five years", unit: "municipalities" },
          { id: "1_1_d", label: "Municipalities outside Scotts Bluff Co. with a housing project funded, permitted, or under construction", unit: "municipalities" },
        ],
      },
      {
        id: "obj1_2", label: "Obj 1.2 — Childcare (target: halve communities with zero licensed childcare by 2030)",
        kpis: [
          { id: "1_2_a", label: "Licensed childcare slots region-wide, all license types (DHHS)", unit: "slots" },
          { id: "1_2_b", label: "Municipalities with no licensed childcare provider within municipal limits", unit: "municipalities", lowerIsBetter: true },
          { id: "1_2_c", label: "Employer-supported childcare partnerships formed, cumulative", unit: "partnerships" },
          { id: "1_2_d", label: "New childcare operations reaching licensure with PADD support", unit: "operations", defaultTarget: "20" },
        ],
      },
    ],
  },
  {
    id: "g2", num: "Goal 2", color: C.g2,
    title: "Grow & Keep Workforce",
    sub: "Succession & Pipeline",
    objectives: [
      {
        id: "obj2_1", label: "Obj 2.1 — Business Succession (target: 15 documented transitions by 2030)",
        kpis: [
          { id: "2_1_a", label: "Businesses enrolled in the Succession Registry, cumulative", unit: "businesses", defaultTarget: "25" },
          { id: "2_1_c", label: "Documented ownership transitions completed, cumulative", unit: "transitions", defaultTarget: "15" },
          { id: "2_1_d", label: "Jobs retained through completed transitions (FTE at transition date)", unit: "FTE" },
          { id: "2_1_f", label: "Enrolled businesses connected to a transition specialist within 90 days", unit: "%", defaultTarget: "90" },
        ],
      },
      {
        id: "obj2_2", label: "Obj 2.2 — Workforce Pipeline (target: +50% over Year 1 baseline by 2030)",
        kpis: [
          { id: "2_2_a", label: "Active apprenticeships and work-based learning agreements with Panhandle employers", unit: "agreements" },
          { id: "2_2_b", label: "Unique employers participating in pipeline programs annually", unit: "employers" },
          { id: "2_2_c", label: "Participants employed in the Panhandle one year after completion (report with cohort response rate)", unit: "%" },
        ],
      },
    ],
  },
  {
    id: "g3", num: "Goal 3", color: C.g3,
    title: "Modernize Infrastructure",
    sub: "Water/Wastewater & Broadband",
    objectives: [
      {
        id: "obj3_1", label: "Obj 3.1 — Water & Wastewater (target: 10 projects funded and started by 2030)",
        kpis: [
          { id: "3_1_d", label: "Infrastructure grant applications submitted annually (PADD-prepared and PADD-assisted)", unit: "apps/yr" },
          { id: "3_1_e", label: "Infrastructure grant applications awarded annually (award rate target range set at Year 1 review)", unit: "awards/yr", partOf: "3_1_d" },
          { id: "3_1_f", label: "Total project investment leveraged through awarded applications, incl. local match", unit: "$" },
          { id: "3_1_g", label: "Water or wastewater projects with construction initiated or complete, cumulative", unit: "projects", defaultTarget: "10" },
        ],
      },
      {
        id: "obj3_2", label: "Obj 3.2 — Broadband (target: halve locations without 100/20 Mbps from Year 1 baseline)",
        kpis: [
          { id: "3_2_g", label: "Serviceable locations without 100/20 Mbps via terrestrial reliable broadband (FCC BDC)", unit: "locations", lowerIsBetter: true },
          { id: "3_2_h", label: "Locations where satellite is the only service meeting 100/20 Mbps (FCC BDC)", unit: "locations" },
          { id: "3_2_i", label: "Households with a broadband internet subscription (ACS B28002)", unit: "%" },
        ],
      },
    ],
  },
  {
    id: "g4", num: "Goal 4", color: C.g4,
    title: "Regional Capacity & Resilience",
    sub: "Coordination & Natural Resources",
    objectives: [
      {
        id: "obj4_1", label: "Obj 4.1 — Regional Coordination, Capacity & Accountability",
        kpis: [
          { id: "4_1_a", label: "CEDS Strategy Council meetings held annually", unit: "meetings/yr", defaultTarget: "2" },
          { id: "4_1_b", label: "Annual CEDS reviews completed and published publicly", unit: "reviews", defaultTarget: "5" },
          { id: "4_1_f", label: "Counties (of 11) with at least one documented CEDS activity this year", unit: "counties", defaultTarget: "11" },
          { id: "4_1_g", label: "Funding alerts distributed through the Panhandle Opportunity Tracker", unit: "alerts" },
          { id: "4_1_h", label: "Communities under 1,000 population with a documented CEDS activity, cumulative", unit: "communities", defaultTarget: "6" },
          { id: "4_1_d", label: "Performance measures with a current-year value reported at annual review", unit: "#/31", defaultTarget: "27", trackerKpi: true },
        ],
      },
      {
        id: "obj4_2", label: "Obj 4.2 — Natural Resource & Economic Resilience (target: 3 regional initiatives by 2030)",
        kpis: [
          { id: "4_2_d", label: "Aquifer trend reports published and distributed to regional stakeholders", unit: "reports" },
          { id: "4_2_e", label: "Renewable-energy feasibility studies completed in Panhandle counties", unit: "studies", defaultTarget: "2" },
          { id: "4_2_f", label: "Multi-jurisdictional tourism initiatives implemented", unit: "initiatives" },
        ],
      },
    ],
  },
];

const YEARS = ["2026", "2027", "2028", "2029", "2030"];
// Older versions of this page saved edits to the browser first; values left
// there are offered to staff for upload (see "localOnly" below).
const LS_KEY = "ceds_kpi_values_v1";
const FIELDS = ["baseline", "yr2026", "yr2027", "yr2028", "yr2029", "yr2030", "target"];

// Staff who may edit. The database enforces the same list (RLS policies on
// ceds_kpi_values via public.is_ceds_editor()); this copy only decides what
// the page shows.
const EDITORS = ["kylek@nepadd.com", "bryanv@nepadd.com", "davidw@nepadd.com"];
const isEditorEmail = (email) => EDITORS.includes((email || "").trim().toLowerCase());

const toRow = (kpiId, v, email) => ({
  kpi_id:       kpiId,
  baseline:     v.baseline || null,
  yr2026:       v.yr2026   || null,
  yr2027:       v.yr2027   || null,
  yr2028:       v.yr2028   || null,
  yr2029:       v.yr2029   || null,
  yr2030:       v.yr2030   || null,
  target_value: v.target   || null,
  updated_at:   new Date().toISOString(),
  updated_by:   email,
});

// ── Staff sign-in (passwordless email link) ──────────────────────────────────
function StaffSignIn({ user, signOut }) {
  const [open, setOpen]     = useState(false);
  const [email, setEmail]   = useState("");
  const [status, setStatus] = useState(null); // null | "sending" | "sent" | error message

  const linkStyle = { background: "none", border: "none", padding: 0, color: C.slate, fontSize: 12, textDecoration: "underline", cursor: "pointer" };

  if (user) {
    return (
      <p style={{ fontSize: 12, color: C.slate, margin: "0 0 16px" }}>
        Signed in as {user.email}
        {!isEditorEmail(user.email) && " (view only)"}
        {" · "}
        <button style={linkStyle} onClick={signOut}>Sign out</button>
      </p>
    );
  }

  async function send(e) {
    e.preventDefault();
    if (!isEditorEmail(email)) {
      setStatus("That email isn’t on the PADD staff editor list.");
      return;
    }
    setStatus("sending");
    const { error } = await supabase.auth.signInWithOtp({
      email: email.trim().toLowerCase(),
      options: { emailRedirectTo: `${window.location.origin}/?tab=tracker` },
    });
    setStatus(error ? `Couldn’t send the link: ${error.message}` : "sent");
  }

  return (
    <div style={{ margin: "0 0 16px" }}>
      {!open ? (
        <button style={linkStyle} onClick={() => setOpen(true)}>🔒 PADD staff sign-in to edit</button>
      ) : status === "sent" ? (
        <p style={{ fontSize: 12, color: C.slate }}>
          Check your email for a sign-in link (it may land in spam the first time).
        </p>
      ) : (
        <form onSubmit={send} style={{ display: "inline-flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          <label htmlFor="staff-email" style={{ fontSize: 12, color: C.slate }}>PADD staff email</label>
          <input id="staff-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="name@nepadd.com"
            style={{ padding: "4px 8px", fontSize: 12, border: `1px solid ${C.sand}`, borderRadius: 4 }} />
          <button type="submit" disabled={status === "sending"}
            style={{ padding: "4px 12px", fontSize: 12, fontWeight: 600, background: C.terra, color: C.white, border: "none", borderRadius: 4, cursor: "pointer" }}>
            {status === "sending" ? "Sending…" : "Email me a sign-in link"}
          </button>
          {status && status !== "sending" && (
            <div style={{ width: "100%", fontSize: 11, color: "#B94040", marginTop: 4 }}>{status}</div>
          )}
        </form>
      )}
    </div>
  );
}

// ── Ring donut chart ──────────────────────────────────────────────────────────
function Ring({ pct, color, size = 72 }) {
  const r = size / 2 - 7;
  const circ = 2 * Math.PI * r;
  const dash = (Math.min(Math.max(pct, 0), 100) / 100) * circ;
  return (
    <svg width={size} height={size} aria-hidden="true" style={{ flexShrink: 0 }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={C.sand} strokeWidth="6" />
      <circle
        cx={size / 2} cy={size / 2} r={r}
        fill="none" stroke={color} strokeWidth="6"
        strokeDasharray={`${dash} ${circ}`}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: "stroke-dasharray 0.5s ease" }}
      />
      <text
        x={size / 2} y={size / 2 + 5}
        textAnchor="middle"
        fill={C.navy}
        fontSize="13"
        fontWeight="700"
        fontFamily="var(--font-sans)"
      >
        {Math.round(pct)}%
      </text>
    </svg>
  );
}

// ── Progress bar ──────────────────────────────────────────────────────────────
function ProgressBar({ pct, color }) {
  const safe = Math.min(Math.max(pct, 0), 100);
  return (
    <div style={{ background: C.sand, borderRadius: 99, height: 6, minWidth: 72, overflow: "hidden" }}>
      <div style={{ height: 6, width: `${safe}%`, background: color, borderRadius: 99, transition: "width 0.4s ease" }} />
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function CedsTracker() {
  // values: { [kpiId]: { baseline, yr2026..yr2030, target } }
  const [values, setValues]       = useState({});
  const [editing, setEditing]     = useState(null); // kpiId currently being edited
  const [draft, setDraft]         = useState({});
  const [activeGoal, setActiveGoal] = useState(null); // null = all goals
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [saveError, setSaveError] = useState(null);
  // values saved only in this browser by the old version of the page
  const [localOnly, setLocalOnly] = useState({});
  const { user, signOut } = useAuth();
  const isEditor = isEditorEmail(user?.email);

  // ── Load from Supabase (the shared record) ───────────────────────────────
  useEffect(() => {
    async function load() {
      setLoading(true);
      const db = {};
      const { data, error } = await supabase.from("ceds_kpi_values").select("*");
      if (!error && data) {
        data.forEach((row) => {
          db[row.kpi_id] = {
            baseline: row.baseline ?? "",
            yr2026:   row.yr2026   ?? "",
            yr2027:   row.yr2027   ?? "",
            yr2028:   row.yr2028   ?? "",
            yr2029:   row.yr2029   ?? "",
            yr2030:   row.yr2030   ?? "",
            target:   row.target_value ?? "",
          };
        });
      }
      setValues(db);

      // Values an earlier version of this page left only in this browser
      try {
        const local = JSON.parse(localStorage.getItem(LS_KEY) || "{}");
        const pending = {};
        for (const [id, v] of Object.entries(local)) {
          const differs = FIELDS.some((k) => (v?.[k] ?? "") !== "" && (v?.[k] ?? "") !== (db[id]?.[k] ?? ""));
          if (differs) pending[id] = v;
        }
        setLocalOnly(pending);
      } catch {}

      setLoading(false);
    }
    load();
  }, []);

  // ── Helpers ───────────────────────────────────────────────────────────────
  function getVal(kpiId, field) {
    return values[kpiId]?.[field] ?? "";
  }

  function getTarget(kpi) {
    const stored = values[kpi.id]?.target;
    return stored !== undefined && stored !== "" ? stored : (kpi.defaultTarget ?? "");
  }

  function getLatest(kpiId) {
    const v = values[kpiId];
    if (!v) return "";
    for (const yr of ["yr2030", "yr2029", "yr2028", "yr2027", "yr2026"]) {
      if (v[yr] && v[yr] !== "") return v[yr];
    }
    return v.baseline ?? "";
  }

  function calcProgress(kpi) {
    const latest = parseFloat(getLatest(kpi.id));
    if (isNaN(latest)) return 0;
    if (kpi.trackerKpi) return Math.min(100, (latest / 31) * 100);
    const target = parseFloat(getTarget(kpi));
    if (isNaN(target) || target === 0) return 0;

    if (kpi.lowerIsBetter) {
      const baseline = parseFloat(getVal(kpi.id, "baseline"));
      if (!isNaN(baseline) && baseline !== target && baseline > target) {
        return Math.max(0, Math.min(100, ((baseline - latest) / (baseline - target)) * 100));
      }
      return latest <= target ? 100 : Math.max(0, (target / latest) * 100);
    }

    return Math.min(100, (latest / target) * 100);
  }

  function calcGoalProgress(goal) {
    const kpis = goal.objectives.flatMap((o) => o.kpis);
    const measurable = kpis.filter((k) => {
      const t = parseFloat(getTarget(k));
      const l = parseFloat(getLatest(k.id));
      return !isNaN(t) && !isNaN(l) && t !== 0;
    });
    if (!measurable.length) return 0;
    return measurable.reduce((sum, k) => sum + calcProgress(k), 0) / measurable.length;
  }

  // ── Edit handlers ─────────────────────────────────────────────────────────
  function startEdit(kpi) {
    setEditing(kpi.id);
    setDraft({
      baseline: getVal(kpi.id, "baseline"),
      yr2026:   getVal(kpi.id, "yr2026"),
      yr2027:   getVal(kpi.id, "yr2027"),
      yr2028:   getVal(kpi.id, "yr2028"),
      yr2029:   getVal(kpi.id, "yr2029"),
      yr2030:   getVal(kpi.id, "yr2030"),
      target:   getTarget(kpi),
    });
  }

  async function saveEdit(kpi) {
    setSaving(true);
    setSaveError(null);
    const { error } = await supabase
      .from("ceds_kpi_values")
      .upsert(toRow(kpi.id, draft, user?.email), { onConflict: "kpi_id" });
    setSaving(false);
    if (error) {
      setSaveError(`Not saved: ${error.message}`);
      return; // keep the row open so nothing typed is lost
    }
    setValues((v) => ({ ...v, [kpi.id]: { ...draft } }));
    setEditing(null);
    setDraft({});
  }

  async function uploadLocalOnly() {
    setSaving(true);
    setSaveError(null);
    const rows = Object.entries(localOnly).map(([id, v]) => toRow(id, v, user?.email));
    const { error } = await supabase.from("ceds_kpi_values").upsert(rows, { onConflict: "kpi_id" });
    setSaving(false);
    if (error) {
      setSaveError(`Upload failed: ${error.message}`);
      return;
    }
    setValues((v) => ({ ...v, ...localOnly }));
    setLocalOnly({});
    try { localStorage.removeItem(LS_KEY); } catch {}
  }

  function discardLocalOnly() {
    setLocalOnly({});
    try { localStorage.removeItem(LS_KEY); } catch {}
  }

  function cancelEdit() {
    setEditing(null);
    setDraft({});
  }

  const visibleGoals = activeGoal ? GOALS.filter((g) => g.id === activeGoal) : GOALS;

  // ── Inline styles ──────────────────────────────────────────────────────────
  const s = {
    h1: { fontFamily: "var(--font-serif)", fontSize: 26, fontWeight: 700, color: C.navy, margin: "0 0 6px" },
    subtitle: { fontSize: 13, color: "#5A5A5A", margin: "0 0 28px", lineHeight: 1.6 },

    goalCards: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
      gap: 12,
      marginBottom: 28,
    },
    goalCard: (active, color) => ({
      background: C.white,
      border: `2px solid ${active ? color : C.sand}`,
      borderRadius: 12,
      padding: "16px",
      cursor: "pointer",
      transition: "border-color 0.15s, box-shadow 0.15s",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 8,
      boxShadow: active ? `0 0 0 3px ${color}28` : "none",
      userSelect: "none",
    }),
    goalNum: (active, color) => ({
      fontSize: 10, fontWeight: 700, textTransform: "uppercase",
      letterSpacing: "0.07em", color: active ? color : "#999",
    }),
    goalTitle: { fontSize: 13, fontWeight: 600, color: C.navy, textAlign: "center", lineHeight: 1.35 },
    goalSub:   { fontSize: 11, color: "#888", textAlign: "center" },

    filterRow: { display: "flex", alignItems: "center", gap: 8, marginBottom: 24, flexWrap: "wrap" },
    filterLbl: { fontSize: 12, color: "#888", fontWeight: 500 },
    filterBtn: (active) => ({
      padding: "4px 14px", borderRadius: 20,
      border: `1px solid ${active ? C.terra : C.sand}`,
      background: active ? C.terra : C.white,
      color: active ? C.white : C.slate,
      fontSize: 12, fontWeight: 600, cursor: "pointer",
    }),

    goalBlock: { marginBottom: 44 },
    goalStripe: (color) => ({ width: 4, height: 28, background: color, borderRadius: 2, flexShrink: 0 }),
    goalHeading: { display: "flex", alignItems: "center", gap: 12, marginBottom: 20 },
    goalNumLabel: (color) => ({ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color }),
    goalTitleLg: { fontSize: 17, fontWeight: 700, fontFamily: "var(--font-serif)", color: C.navy, lineHeight: 1.2 },

    objSection: { marginBottom: 28 },
    objHeader: {
      display: "flex", alignItems: "center", gap: 8,
      marginBottom: 10, paddingBottom: 7,
      borderBottom: `1.5px solid ${C.cream}`,
    },
    objLabel: { fontSize: 12, fontWeight: 700, color: C.slate, textTransform: "uppercase", letterSpacing: "0.05em" },

    tableWrap: { overflowX: "auto" },
    table: {
      width: "100%", borderCollapse: "collapse",
      background: C.white, border: `1px solid ${C.sand}`,
      borderRadius: 10, overflow: "hidden", fontSize: 12,
    },
    th: (left) => ({
      background: C.cream, color: C.slate, fontWeight: 600,
      fontSize: 10, letterSpacing: "0.04em", whiteSpace: "nowrap",
      padding: "7px 10px", borderBottom: `1px solid ${C.sand}`,
      textAlign: left ? "left" : "center",
    }),
    td: (left, last, editing) => ({
      padding: "8px 10px",
      borderBottom: last ? "none" : `1px solid ${C.sand}`,
      textAlign: left ? "left" : "center",
      color: C.navy, verticalAlign: "middle",
      background: editing ? "#FFF8F5" : "transparent",
    }),
    unitBadge: {
      fontSize: 10, color: "#767676", background: C.warm,
      border: `1px solid ${C.sand}`, borderRadius: 4, padding: "1px 5px",
    },
    cellInput: {
      width: "100%", minWidth: 52, padding: "3px 6px",
      fontSize: 12, border: `1px solid ${C.terra}`,
      borderRadius: 4, background: "#FFF8F5", color: C.navy,
      textAlign: "center", fontFamily: "var(--font-sans)",
      boxSizing: "border-box",
    },
    editBtn:   { padding: "3px 10px", fontSize: 11, fontWeight: 600, background: C.white, border: `1px solid ${C.sand}`, borderRadius: 4, color: C.slate, cursor: "pointer" },
    saveBtn:   { padding: "3px 10px", fontSize: 11, fontWeight: 600, background: C.terra, border: "none", borderRadius: 4, color: C.white, cursor: "pointer", marginRight: 4 },
    cancelBtn: { padding: "3px 10px", fontSize: 11, fontWeight: 600, background: C.white, border: `1px solid ${C.sand}`, borderRadius: 4, color: "#888", cursor: "pointer" },
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "60px 0", color: "#888", fontSize: 14 }}>
        Loading tracker data…
      </div>
    );
  }

  return (
    <div>
      <h1 style={s.h1}>CEDS Performance Tracker</h1>
      <p style={s.subtitle}>
        The 31 performance measures across 4 goals and 8 objectives in Chapter 9 of the 2026–2031 CEDS.
        {isEditor && <> Click <strong>Edit</strong> on any row to enter or update values.</>}
      </p>
      <StaffSignIn user={user} signOut={signOut} />

      {isEditor && Object.keys(localOnly).length > 0 && (
        <div className="callout" style={{ marginBottom: 20 }}>
          <span className="callout-label">Values saved only in this browser</span>
          <p style={{ margin: "0 0 10px" }}>
            This browser has values for {Object.keys(localOnly).length} measure
            {Object.keys(localOnly).length === 1 ? "" : "s"} that were never saved to the shared tracker
            (an earlier version of this page kept edits locally). Upload them so everyone sees them?
          </p>
          <button style={{ ...s.saveBtn, padding: "5px 14px", fontSize: 12 }} onClick={uploadLocalOnly} disabled={saving}>
            {saving ? "Uploading…" : "Upload to shared tracker"}
          </button>
          <button style={{ ...s.cancelBtn, padding: "5px 14px", fontSize: 12 }} onClick={discardLocalOnly}>
            Discard
          </button>
        </div>
      )}

      {saveError && (
        <p role="alert" style={{ fontSize: 12, color: "#B94040", margin: "0 0 16px" }}>{saveError}</p>
      )}

      {/* ── Goal summary cards ─────────────────────────────────────────── */}
      <div style={s.goalCards}>
        {GOALS.map((g) => {
          const pct    = calcGoalProgress(g);
          const active = activeGoal === g.id;
          return (
            <div
              key={g.id}
              style={s.goalCard(active, g.color)}
              role="button"
              aria-pressed={active}
              tabIndex={0}
              onClick={() => setActiveGoal(active ? null : g.id)}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setActiveGoal(active ? null : g.id)}
              onMouseEnter={(e) => { if (!active) e.currentTarget.style.borderColor = g.color; }}
              onMouseLeave={(e) => { if (!active) e.currentTarget.style.borderColor = C.sand; }}
            >
              <span style={s.goalNum(active, g.color)}>{g.num}</span>
              <Ring pct={pct} color={g.color} size={72} />
              <div style={s.goalTitle}>{g.title}</div>
              <div style={s.goalSub}>{g.sub}</div>
            </div>
          );
        })}
      </div>

      {/* ── Filter bar ────────────────────────────────────────────────────── */}
      <div style={s.filterRow}>
        <span style={s.filterLbl}>Filter:</span>
        <button style={s.filterBtn(!activeGoal)} onClick={() => setActiveGoal(null)}>All goals</button>
        {GOALS.map((g) => (
          <button key={g.id} style={s.filterBtn(activeGoal === g.id)} onClick={() => setActiveGoal(activeGoal === g.id ? null : g.id)}>
            {g.num}
          </button>
        ))}
      </div>

      {/* ── KPI tables ────────────────────────────────────────────────────── */}
      {visibleGoals.map((goal) => (
        <div key={goal.id} style={s.goalBlock}>
          {/* Goal heading */}
          <div style={s.goalHeading}>
            <div style={s.goalStripe(goal.color)} />
            <div>
              <div style={s.goalNumLabel(goal.color)}>{goal.num}</div>
              <div style={s.goalTitleLg}>{goal.title} — {goal.sub}</div>
            </div>
          </div>

          {goal.objectives.map((obj) => (
            <div key={obj.id} style={s.objSection}>
              <div style={s.objHeader}>
                <span style={s.objLabel}>{obj.label}</span>
              </div>

              <div style={s.tableWrap}>
                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={{ ...s.th(true), width: "22%" }}>KPI</th>
                      <th style={s.th(false)}>Unit</th>
                      <th style={s.th(false)}>Baseline</th>
                      {YEARS.map((y) => <th key={y} style={s.th(false)}>{y}</th>)}
                      <th style={s.th(false)}>Target</th>
                      <th style={{ ...s.th(false), minWidth: 96 }}>Progress</th>
                      {isEditor && <th style={s.th(false)} />}
                    </tr>
                  </thead>
                  <tbody>
                    {obj.kpis.map((kpi, i) => {
                      const isEditing = editing === kpi.id;
                      const isLast    = i === obj.kpis.length - 1;
                      const pct       = calcProgress(kpi);
                      const hasData   = getLatest(kpi.id) !== "" && getTarget(kpi) !== "";

                      const rowBg = isEditing
                        ? "#FFF8F5"
                        : i % 2 === 0 ? C.white : C.warm;

                      return (
                        <tr key={kpi.id} style={{ background: rowBg }}>

                          {/* KPI label */}
                          <td style={{ ...s.td(true, isLast, isEditing), lineHeight: 1.4 }}>
                            {kpi.label}
                            {kpi.lowerIsBetter && (
                              <div style={{ fontSize: 9, color: "#999", marginTop: 2 }}>↓ lower is better</div>
                            )}
                          </td>

                          {/* Unit */}
                          <td style={s.td(false, isLast, isEditing)}>
                            <span style={s.unitBadge}>{kpi.unit}</span>
                          </td>

                          {/* Baseline */}
                          <td style={s.td(false, isLast, isEditing)}>
                            {isEditing ? (
                              <input style={s.cellInput} value={draft.baseline}
                                onChange={(e) => setDraft((d) => ({ ...d, baseline: e.target.value }))}
                                placeholder="—" />
                            ) : (
                              <span style={{ color: getVal(kpi.id, "baseline") ? C.navy : "#CCC" }}>
                                {getVal(kpi.id, "baseline") || "—"}
                              </span>
                            )}
                          </td>

                          {/* Year columns */}
                          {YEARS.map((yr) => {
                            const field = `yr${yr}`;
                            return (
                              <td key={yr} style={s.td(false, isLast, isEditing)}>
                                {isEditing ? (
                                  <input style={s.cellInput} value={draft[field]}
                                    onChange={(e) => setDraft((d) => ({ ...d, [field]: e.target.value }))}
                                    placeholder="—" />
                                ) : (
                                  <span style={{ color: getVal(kpi.id, field) ? C.navy : "#DDD" }}>
                                    {getVal(kpi.id, field) || "—"}
                                  </span>
                                )}
                              </td>
                            );
                          })}

                          {/* Target */}
                          <td style={s.td(false, isLast, isEditing)}>
                            {isEditing ? (
                              <input
                                style={{ ...s.cellInput, borderColor: goal.color }}
                                value={draft.target}
                                onChange={(e) => setDraft((d) => ({ ...d, target: e.target.value }))}
                                placeholder="—"
                              />
                            ) : (
                              <span style={{ color: getTarget(kpi) ? goal.color : "#DDD", fontWeight: getTarget(kpi) ? 600 : 400 }}>
                                {getTarget(kpi) || "—"}
                              </span>
                            )}
                          </td>

                          {/* Progress */}
                          <td style={s.td(false, isLast, isEditing)}>
                            {hasData ? (
                              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
                                <ProgressBar pct={pct} color={goal.color} />
                                <span style={{ fontSize: 10, color: "#767676" }}>{Math.round(pct)}%</span>
                              </div>
                            ) : (
                              <span style={{ fontSize: 10, color: "#CCC" }}>no data</span>
                            )}
                          </td>

                          {/* Edit / Save / Cancel (staff only) */}
                          {isEditor && <td style={{ ...s.td(false, isLast, isEditing), whiteSpace: "nowrap" }}>
                            {isEditing ? (
                              <>
                                <button style={s.saveBtn} onClick={() => saveEdit(kpi)} disabled={saving}>
                                  {saving ? "…" : "Save"}
                                </button>
                                <button style={s.cancelBtn} onClick={cancelEdit}>✕</button>
                              </>
                            ) : (
                              <button style={s.editBtn} onClick={() => startEdit(kpi)}>Edit</button>
                            )}
                          </td>}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      ))}

      <p style={{ fontSize: 10, color: "#BBB", textAlign: "center", marginTop: 8 }}>
        Values entered by PADD staff · 2026–2031 CEDS cycle
      </p>
    </div>
  );
}
